import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Video, Phone, Send, Mail, MessageSquare } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

export function FaceTimeInviteDialog({ open, onClose, lead }) {
  const { user } = useAuth();
  const [channel, setChannel] = useState('sms');
  const [mode, setMode] = useState('video');
  const [facetimeId, setFacetimeId] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Pre-fill FaceTime ID with the member's own phone (preferred) or email.
  useEffect(() => {
    if (open && user) {
      const defaultId = (user.phone || '').trim() || (user.email || '').trim();
      setFacetimeId(defaultId);
    }
  }, [open, user]);

  useEffect(() => {
    if (!open) return;
    // Auto-pick the best channel based on lead contact info
    if (!lead?.phone && lead?.email) setChannel('email');
    else if (lead?.phone) setChannel('sms');
  }, [open, lead]);

  const link = `${mode === 'audio' ? 'facetime-audio' : 'facetime'}:${facetimeId.trim()}`;
  const hasPhone = !!(lead?.phone || '').trim();
  const hasEmail = !!(lead?.email || '').trim();

  const send = async () => {
    if (sending) return;
    if (!facetimeId.trim()) { toast.error('Your FaceTime contact (phone or email) is required'); return; }
    if (channel === 'sms' && !hasPhone) { toast.error('Lead has no phone number'); return; }
    if (channel === 'email' && !hasEmail) { toast.error('Lead has no email address'); return; }
    setSending(true);
    try {
      const { data } = await api.sendFaceTimeInvite(lead.id, {
        channel,
        facetime_id: facetimeId.trim(),
        mode,
        message: message.trim() || null,
      });
      toast.success(`FaceTime invite sent via ${data.channel.toUpperCase()} to ${data.to}`);
      setMessage('');
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="facetime-invite-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Video className="w-4 h-4 text-[#5856D6]" />
            Invite {lead?.name || 'lead'} to FaceTime
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs font-medium">Send via</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              <button
                type="button"
                onClick={() => setChannel('sms')}
                disabled={!hasPhone}
                className={`flex items-center justify-center gap-2 px-3 py-2 text-xs rounded-sm border transition-colors ${
                  channel === 'sms'
                    ? 'border-[#5856D6] bg-[#5856D6]/5 text-[#5856D6]'
                    : 'border-slate-300 text-slate-700 hover:border-[#5856D6]/40'
                } ${!hasPhone ? 'opacity-40 cursor-not-allowed' : ''}`}
                data-testid="facetime-channel-sms"
              >
                <MessageSquare className="w-3.5 h-3.5" /> SMS to {lead?.phone || 'no phone'}
              </button>
              <button
                type="button"
                onClick={() => setChannel('email')}
                disabled={!hasEmail}
                className={`flex items-center justify-center gap-2 px-3 py-2 text-xs rounded-sm border transition-colors ${
                  channel === 'email'
                    ? 'border-[#5856D6] bg-[#5856D6]/5 text-[#5856D6]'
                    : 'border-slate-300 text-slate-700 hover:border-[#5856D6]/40'
                } ${!hasEmail ? 'opacity-40 cursor-not-allowed' : ''}`}
                data-testid="facetime-channel-email"
              >
                <Mail className="w-3.5 h-3.5" /> Email to {lead?.email || 'no email'}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-xs font-medium">Call mode</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              <button
                type="button"
                onClick={() => setMode('video')}
                className={`flex items-center justify-center gap-2 px-3 py-2 text-xs rounded-sm border transition-colors ${
                  mode === 'video' ? 'border-[#5856D6] bg-[#5856D6]/5 text-[#5856D6]' : 'border-slate-300 text-slate-700 hover:border-[#5856D6]/40'
                }`}
                data-testid="facetime-mode-video"
              >
                <Video className="w-3.5 h-3.5" /> Video
              </button>
              <button
                type="button"
                onClick={() => setMode('audio')}
                className={`flex items-center justify-center gap-2 px-3 py-2 text-xs rounded-sm border transition-colors ${
                  mode === 'audio' ? 'border-[#5856D6] bg-[#5856D6]/5 text-[#5856D6]' : 'border-slate-300 text-slate-700 hover:border-[#5856D6]/40'
                }`}
                data-testid="facetime-mode-audio"
              >
                <Phone className="w-3.5 h-3.5" /> Audio
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="ft-id" className="text-xs font-medium">Your FaceTime contact</Label>
            <Input
              id="ft-id"
              value={facetimeId}
              onChange={(e) => setFacetimeId(e.target.value)}
              placeholder="+1 555 123 4567 or you@icloud.com"
              className="mt-1 text-sm rounded-sm"
              data-testid="facetime-id-input"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              The phone or Apple ID email your FaceTime account uses. The lead will tap a link that opens FaceTime calling you.
            </p>
          </div>

          <div>
            <Label htmlFor="ft-msg" className="text-xs font-medium">Personal note (optional)</Label>
            <Textarea
              id="ft-msg"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g. Quick chat about your application?"
              rows={2}
              maxLength={500}
              className="mt-1 text-sm rounded-sm"
              data-testid="facetime-message-input"
            />
          </div>

          <div className="p-3 rounded-sm bg-slate-50 border border-slate-200">
            <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium mb-1">Link preview</p>
            <code className="text-[11px] text-slate-700 break-all font-mono">{link}</code>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={sending} data-testid="facetime-cancel-btn">
            Cancel
          </Button>
          <Button
            size="sm"
            className="bg-[#5856D6] hover:bg-[#4F46E5]"
            onClick={send}
            disabled={sending || !facetimeId.trim() || (channel === 'sms' ? !hasPhone : !hasEmail)}
            data-testid="facetime-send-invite-btn"
          >
            {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
            Send invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
